import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';

import FieldsetList from '../FieldSetList';
import Popup from '../../common/Popup';
import EmailConfirmation from '../EmailConfirmation';
import MobilePhoneConfirmation from '../MobilePhoneConfirmation';

import { isDesktop } from '../../../utils/mobileHelpers';
import { PROFILE, COMMON_ERROR_MESSAGES } from '../../../constants/strings';
import { COLORS } from '../../../constants/styles';
import { getFieldsetDescriptor } from '../utils/getFieldset';

import { enhance } from './enhance';

class ContactInfoSection extends PureComponent {
  state = {
    isConfirmationPopupOpened: false,
    error: null,
  };

  componentWillReceiveProps(nextProps) {
    const {
      sending: prevConfirmPhoneRequestSending,
    } = this.props.confirmMobilePhoneRequest;
    const {
      sending: confirmPhoneRequestSending,
      succeed: confirmPhoneRequestSucceed,
    } = nextProps.confirmMobilePhoneRequest;
    const {
      editMobilePhoneRequest: prevEditMobilePhoneRequest,
      editEmailRequest: prevEditEmailRequest,
      profile,
      clearEmailRequest,
      clearPhoneRequest,
    } = this.props;
    const {
      editMobilePhoneRequest,
      editEmailRequest,
      profile: nextProfile,
    } = nextProps;

    const emailSuccess =
      prevEditEmailRequest.sending &&
      !editEmailRequest.sending &&
      editEmailRequest.succeed;
    const phoneSuccess =
      prevEditMobilePhoneRequest.sending &&
      !editMobilePhoneRequest.sending &&
      editMobilePhoneRequest.succeed;

    let newState = {};

    if (emailSuccess || phoneSuccess) {
      newState = {
        isConfirmationPopupOpened: true,
        succeedTargets: {
          email: get(this.state, 'succeedTargets.email') || emailSuccess,
          phone: get(this.state, 'succeedTargets.phone') || phoneSuccess,
        },
      };
    }

    if (
      prevConfirmPhoneRequestSending &&
      !confirmPhoneRequestSending &&
      confirmPhoneRequestSucceed
    ) {
      Object.assign(newState, {
        isConfirmationPopupOpened: false,
        phoneNumber: this.state.targets.phoneNumber,
      });
    }

    if (get(profile, 'id') !== get(nextProfile, 'id')) {
      clearPhoneRequest();
      clearEmailRequest();
    }

    this.setState(newState);
  }

  getEmailConfirmationStyle = () => {
    const { email, phoneNumber } = this.state.targets;
    if (!email || !phoneNumber || !this.state.succeedTargets.phone) return {};

    return {
      borderBottom: `1px solid ${COLORS.grayBluish}`,
      paddingBottom: 15,
      marginBottom: 15,
    };
  };

  onCancel = () => {
    this.props.clearEmailRequest();
    this.props.clearPhoneRequest();
  };

  get errors() {
    const {
      editMobilePhoneRequest,
      editEmailRequest,
      intl: { formatMessage },
    } = this.props;
    const errors = [];

    if (editEmailRequest.rejected) {
      const { code } = get(
        editEmailRequest,
        'error.meta.validationErrors.email[0]',
        {},
      );
      const emailErrorMsgId = code
        ? PROFILE.errors.email[code]
        : COMMON_ERROR_MESSAGES.error;
      errors.push(formatMessage(emailErrorMsgId));
    }

    if (editMobilePhoneRequest.rejected) {
      const { code } = get(
        editMobilePhoneRequest,
        'error.meta.validationErrors.phoneNumber[0]',
        {},
      );
      const phoneErrorMsgId = code
        ? PROFILE.errors.phoneNumber[code]
        : COMMON_ERROR_MESSAGES.error;
      errors.push(formatMessage(phoneErrorMsgId));
    }

    return errors;
  }

  openPopupConfirmation = isOpened => {
    this.setState({
      isConfirmationPopupOpened: isOpened,
    });

    if (!isOpened) this.props.clearMobilePhoneConfirmation();
  };

  handleConfirmPhone = confirmationCode => {
    const {
      profile: { id },
    } = this.props;
    this.props.confirmMobilePhone({ confirmationCode, id });
  };

  handleSave = fields => {
    const previousPhone = this.state.phoneNumber || this.props.phoneNumber;
    const targets = {
      email: fields.email !== this.props.email ? fields.email : null,
      phoneNumber:
        fields.phoneNumber !== previousPhone ? fields.phoneNumber : null,
    };

    const hasChanges = [targets.email, targets.phoneNumber].some(i => i);

    if (hasChanges && this.props.canEditPatient) {
      const id = get(this.props.profile, 'primaryPatientProfile.id');
      const data = Object.keys(targets).reduce((acc, key) => {
        targets[key] && (acc[key] = targets[key]);

        return acc;
      }, {});

      this.props.savePatientByProvider({ ...data, id });
    } else if (hasChanges) {
      const targetUserId = get(this.props.profile, 'id');
      if (targets.email) {
        this.props.updateEmail({
          email: targets.email,
          returnUrl: location.href,
          id: targetUserId,
        });
      }
      if (targets.phoneNumber) {
        this.props.updateMobilePhone({
          phoneNumberCountryIsoCode: this.props.currentCountryCode,
          phoneNumber: targets.phoneNumber,
          id: targetUserId,
        });
      }

      this.setState({
        targets,
      });
    } else {
      this.onCancel();
    }
  };

  render() {
    const {
      intl,
      confirmMobilePhoneRequest,
      canEdit,
      canEditPatient,
      intl: { formatMessage },
    } = this.props;
    const { isConfirmationPopupOpened, targets, succeedTargets } = this.state;
    const { props, errors } = this;

    return (
      <div styleName="contact-info-section">
        <FieldsetList
          disabled={!(canEdit || canEditPatient)}
          fieldSetList={getFieldsetDescriptor({
            ...props,
            errors,
          })}
          onSave={this.handleSave}
          onCancelCallback={this.onCancel}
          canEdit={canEdit}
        />
        <Popup
          opened={isConfirmationPopupOpened}
          onClose={() => this.openPopupConfirmation(false)}
          width={isDesktop() ? 'small' : 'medium'}
          title={formatMessage(PROFILE.edit.emailConfirmationTitle)}
        >
          <If condition={isConfirmationPopupOpened}>
            <section styleName="confirmation-popup-content">
              <If condition={targets.email && succeedTargets.email}>
                <EmailConfirmation
                  email={targets.email}
                  showClose={!targets.phoneNumber || !succeedTargets.phone}
                  onClose={() => this.openPopupConfirmation(false)}
                  style={this.getEmailConfirmationStyle()}
                  formatMessage={formatMessage}
                />
              </If>
              <If condition={targets.phoneNumber && succeedTargets.phone}>
                <MobilePhoneConfirmation
                  phone={targets.phoneNumber}
                  onConfirm={this.handleConfirmPhone}
                  onCancel={() => this.openPopupConfirmation(false)}
                  error={confirmMobilePhoneRequest.error}
                  intl={intl}
                />
              </If>
            </section>
          </If>
        </Popup>
      </div>
    );
  }
}

const ReqType = PropTypes.shape({
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  sending: PropTypes.bool,
  succeed: PropTypes.bool,
  rejected: PropTypes.bool,
});

ContactInfoSection.propTypes = {
  email: PropTypes.string,
  phoneNumber: PropTypes.string,
  updateEmail: PropTypes.func,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
  updateMobilePhone: PropTypes.func,
  confirmMobilePhone: PropTypes.func,
  clearMobilePhoneConfirmation: PropTypes.func,
  confirmMobilePhoneRequest: ReqType,
  editMobilePhoneRequest: ReqType,
  editEmailRequest: ReqType,
  clearEmailRequest: PropTypes.func,
  clearPhoneRequest: PropTypes.func,
  canEdit: PropTypes.bool,
  canEditPatient: PropTypes.bool,
  savePatientByProvider: PropTypes.func,
  currentCountryCode: PropTypes.string,
  profile: PropTypes.shape({
    primaryProviderProfile: PropTypes.shape({
      id: PropTypes.number,
    }),
  }),
};

ContactInfoSection.contextTypes = {
  intl: PropTypes.object,
};

export default enhance(ContactInfoSection);



// WEBPACK FOOTER //
// ./src/components/ProfileLayout/ContactInfoSection/index.jsx